// Item iconography for the inventory screen and the quickbar.
//
// DayZ renders 3D thumbnails per item under ONE fixed key light. We cannot ship
// 50 baked renders, so we draw vector items that obey the same rules:
//   * one light direction for the whole set (upper-left key, lower-right core
//     shadow), but the RAMP ITSELF IS PER FORM, not per bounding box — see
//     ICON_FORM / #ic-f-* below. Round 6 painted one objectBoundingBox linear
//     gradient across every silhouette, so a bottle, an apple and a backpack
//     all got the identical diagonal wash across their box: the shading
//     followed the box and never the geometry, which is exactly what makes a
//     sheet read as gel buttons instead of lit objects;
//   * a contact shadow under every object so items sit on the slot plate;
//   * SILHOUETTE, not hue, carries identity. Two items may never share a symbol
//     unless their footprints differ — five cans are five different tins, ten
//     mushrooms are ten different cap profiles, eight berries are eight
//     different clusters. iconCollisions() is the CI gate for that rule.
//
// Contract:
//   ITEM_ICON_DEFS   — the <svg><defs> sheet, injected once into <body>
//   iconFor(id)      — { sym, tint } for an item id; never null (kind fallback)
//   hasIcon(id)      — false when an item only got the generic fallback
//   missingIcons()   — catalog ids with no hand-authored art
//   iconCollisions() — id pairs that share a symbol AND a near-identical tint
//
// Drawing conventions: 32x32 viewBox. Bodies use fill="{f}" and go through B()
// (tint pass + light pass). Detail marks: D/D2 shadow, L/L2 highlight.
import { ITEMS } from '../game/items';

const D = 'rgba(0,0,0,.42)';
const D2 = 'rgba(0,0,0,.64)';
// ART MUST NOT REACH THE DATA TIER. theme.js reserves --ui-hi (#f4f4f1 = 244)
// for elements that CARRY INFORMATION. In round 6 the composite of a pale tint
// + the .34 white key + a .42/.55 specular measured 243 in the hiking-backpack
// region — decorative item art as bright as every quantity numeral on screen.
// Every white in this file is now budgeted so the brightest art pixel lands
// near 215: detail highlights below, the #ic-f-* key stops at .20, and the
// #ic-sp-* speculars at .22/.30/.18/.09. Verified by measuring the inventory
// capture, not by eye (docs/SHOTSTATS.md).
const L = 'rgba(255,255,255,.16)';
const L2 = 'rgba(255,255,255,.26)';
const ST = '#cac3ac';          // mushroom stem / bone / gauze — shared "pale organic"
const ST2 = 'rgba(0,0,0,.28)'; // stem core shadow
const GRN = '#6f8a44';         // leaf / sepal green
const WOOD = '#8a6a42';        // skewers, hafts
const STEEL = '#aeb5b8';

// TINT PASS ONLY. The light no longer rides inside the body: S() lays the form
// ramp, the specular and the contact occlusion over the finished silhouette, so
// the shading is anchored to the object's own axis instead of its bbox.
const B = (shape) => shape.split('{f}').join('currentColor');
// grill/char marks for every "roasted" variant: a charred line with a seared
// highlight riding just above it, so the marks survive on a dark brown body
// (a single .55-black stroke was invisible on roast mutton)
const CHAR = (d) => `<g fill="none" stroke-linecap="round">
  <path d="${d}" stroke="rgba(255,206,140,.26)" stroke-width="3.4" transform="translate(0,-1.3)"/>
  <path d="${d}" stroke="rgba(24,14,8,.9)" stroke-width="2.1"/></g>`;
// ---------------------------------------------------------------------------
// SECOND DETAIL TIER — only for items whose footprint exceeds 1x1.
// Letting an item span its real footprint was round 6's most visible win and it
// created its own defect: a 32x32 body carrying four paths, blown up to roughly
// 124x186 px for a 4x4 pack, presents four flat shapes across a huge area while
// a 1x1 apple presents three across a small one. Detail therefore THINNED OUT
// on exactly the items that occupy the most screen. These marks — seams, zips,
// webbing, hardware, wear — are authored for the big plates and are never used
// on a 1x1 body, where at 56 px they would collapse into noise.
// Every value is inside the same white budget as the rest of the sheet.
const SEAM = (d, w = 0.7) =>
  `<g fill="none" stroke-linecap="round" stroke-dasharray="1.2 1.1" stroke-width="${w}">
     <path d="${d}" stroke="rgba(255,255,255,.10)" transform="translate(0,-.55)"/>
     <path d="${d}" stroke="rgba(0,0,0,.40)"/></g>`;
const ZIP = (d) =>
  `<g fill="none" stroke-linecap="butt">
     <path d="${d}" stroke="rgba(0,0,0,.58)" stroke-width="1.6"/>
     <path d="${d}" stroke="rgba(176,178,170,.42)" stroke-width=".85" stroke-dasharray=".65 .7"/></g>`;
const BUCKLE = (x, y, w = 3.4, h = 2.1) =>
  `<g><rect x="${x}" y="${y}" width="${w}" height="${h}" rx=".45" fill="rgba(20,20,18,.88)"/>
     <rect x="${x}" y="${y}" width="${w}" height=".65" rx=".3" fill="rgba(186,188,180,.34)"/></g>`;
// The sunken blossom end of a berry. Deliberately NOT a five-point crown: at
// icon scale any radial figure resolves back into the cartoon star this is
// replacing, because a star silhouette is what five spokes on a dark disc IS.
// What actually reads as a dimple is a soft well with a lit upper lip and a
// shadowed lower one — the same two-lip rule as any recess under a top-left key.
const CALYX = (cx, cy, r) =>
  `<g><ellipse cx="${cx}" cy="${cy}" rx="${r}" ry="${(r * 0.82).toFixed(2)}" fill="rgba(0,0,0,.26)"/>` +
  `<ellipse cx="${cx}" cy="${(cy + r * 0.16).toFixed(2)}" rx="${(r * 0.6).toFixed(2)}"` +
  ` ry="${(r * 0.46).toFixed(2)}" fill="rgba(0,0,0,.42)"/>` +
  `<path d="M${(cx - r).toFixed(2)} ${(cy + r * 0.1).toFixed(2)} a${r} ${(r * 0.82).toFixed(2)} 0 0 1 ${(r * 2).toFixed(2)} 0"` +
  ` fill="none" stroke="rgba(0,0,0,.34)" stroke-width=".5"/>` +
  `<path d="M${(cx - r * 0.9).toFixed(2)} ${(cy + r * 0.5).toFixed(2)} a${r} ${(r * 0.82).toFixed(2)} 0 0 0 ${(r * 1.8).toFixed(2)} 0"` +
  ` fill="none" stroke="rgba(255,255,255,.11)" stroke-width=".5"/></g>`;
// a rubbed edge or a crease catching the key — the thing that says "used"
const WEAR = (d, w = 1.5) =>
  `<path d="${d}" fill="none" stroke="rgba(255,255,255,.09)" stroke-width="${w}" stroke-linecap="round"/>`;
// wooden skewer behind a roasted forage item
const SKEWER = `<path d="M3.6 28.4L28.4 5.2" stroke="${WOOD}" stroke-width="2.6" stroke-linecap="round"/>
  <path d="M3.6 28.4L28.4 5.2" stroke="rgba(0,0,0,.3)" stroke-width=".9" stroke-linecap="round"/>`;

// ---------------------------------------------------------------------------
// CANVAS ASPECT. Round-5 authored every symbol in a square 32x32 viewBox, so a
// 1x2 water bottle could only ever render as a square glyph letterboxed inside
// a 1x2 plate. The fix is a per-SYMBOL canvas crop (NOT per-item: `bottle_*`
// serves one item each, but `jacket` serves a 2x3 rain jacket AND a 3x3 field
// jacket, and `pants` a 2x3 — a symbol has exactly one shape and therefore
// exactly one legal aspect). Narrow items get their canvas cropped
// symmetrically in x, which keeps the 32-unit height, the #ic-f-* ramp axes
// and the cy=29.2 contact ellipse valid — only the x-extents change.
// Values are width/height. Crops are only ever <1: cropping y would cut the
// contact shadow off the bottom of the canvas.
const ICON_AR = {
  bottle_glass: 0.5, bottle_plastic: 0.5, bottle_murky: 0.5, // all 1x2 items
  pants: 2 / 3,      // work_pants 2x3 (shorts has its own symbol)
  stick: 1 / 3,      // cooking_stick 1x3 — body is re-hung vertically below
};
export const iconAspect = (sym) => ICON_AR[sym] || 1;

// ---------------------------------------------------------------------------
// FORM. This is the shading model, and it is the difference between "lit
// object" and "gel button".
//
// Round 6 lit every body with ONE linearGradient in default objectBoundingBox
// units. Those units re-map the ramp onto whatever box the silhouette happens
// to occupy, so a 1x2 bottle, a round apple and a 4x4 backpack all received the
// same top-left-to-bottom-right wash across their own bbox. The shading tracked
// the box; it never tracked the geometry. Nothing on the sheet could read as
// round, because nothing was shaded as round.
//
// Every ramp below is therefore gradientUnits="userSpaceOnUse", anchored to the
// 32x32 authoring canvas the bodies are actually drawn in, and there is one per
// FORM CLASS rather than one for the whole sheet:
//
//   cyl   upright cylinder — cans, bottles, jars, mugs, shafts. A horizontal
//         ramp across the canvas: dark left edge, key band a third in, core
//         shadow right of centre, and a BOUNCE/RIM stop on the far edge, which
//         is the stop that actually says "this is round and it is standing in
//         a lit room" rather than "this is a rectangle with a bevel".
//   cylh  the same cylinder lying down (flat sardine tin, rolled gauze) —
//         identical ramp rotated to the vertical axis.
//   sph   sphere / dome — apples, berries, mushroom caps, meat, watch faces.
//         A radial with its centre OUTSIDE the body at the key position, so
//         the terminator sweeps around the volume instead of running straight
//         across it, plus a rim stop at the far limb.
//   box   rigid box / packet — cracker boxes, blisters, wallets, crates. Two
//         planes with a deliberately hard break at the ~50% stop: a box's
//         signature is the edge where the lit top plane meets the front, and a
//         soft ramp destroys exactly that read.
//   cloth soft goods — shirts, jackets, pants, packs, rags. Broad, low
//         contrast, no rim: fabric has no specular edge.
//   hard  flat hard goods (blades, firearms, tools) — the old diagonal, but
//         anchored in user space and budgeted down.
//
// MATERIAL rides on top of FORM: a material-keyed SPECULAR, tight and hot for
// steel and glass, wider and dimmer for plastic, a whisper for cloth and
// organics. It is clipped to the object by a luminance mask built from the body
// itself, so it can never leak past the silhouette and dark detail marks
// suppress it for free. Everything not listed reads as cloth/organic.
const ICON_FORM = {
  // upright cylinders
  can_tall: 'cyl', can_wide: 'cyl', can_ribbed: 'cyl', candent: 'cyl',
  drinkcan: 'cyl', jar_fruit: 'cyl', bottle_glass: 'cyl', bottle_plastic: 'cyl',
  bottle_murky: 'cyl', stew_pan: 'cyl', stew_mug: 'cyl', pills: 'cyl',
  ammo_r: 'cyl', ammo_p: 'cyl', stick: 'cyl', bat: 'cyl', spear: 'cyl',
  cigs: 'cyl',
  // cylinders lying down
  tin_flat: 'cylh', bandage: 'cylh',
  // domes / spheres / organic volumes
  apple: 'sph', tape: 'sph', watch: 'sph',
  mu_porcini: 'sph', mu_chant: 'sph', mu_bolete: 'sph', mu_puff: 'sph',
  mu_morel: 'sph', mu_morel_roast: 'sph', mu_agaric: 'sph', mu_death: 'sph',
  mu_false: 'sph', mu_false_roast: 'sph',
  be_blue: 'sph', be_rasp: 'sph', be_lingon: 'sph', be_elder: 'sph',
  be_rose: 'sph', be_rowan: 'sph',
  cut_drum: 'sph', cut_drum_c: 'sph', cut_ribs: 'sph', cut_ribs_c: 'sph',
  cut_steak: 'sph', cut_steak_c: 'sph',
  // rigid boxes and packets
  crackers: 'box', candy: 'box', medbox: 'box', tablets: 'box', wallet: 'box',
  matches: 'box', crate: 'box',
  // soft goods
  tshirt: 'cloth', shirt: 'cloth', jacket: 'cloth', pants: 'cloth',
  shorts: 'cloth', bag_courier: 'cloth', bag_hiking: 'cloth',
  bag_expedition: 'cloth', rags: 'cloth', hand: 'cloth',
  // knife / axe / rifle / pistol fall through to 'hard'
};
const ICON_MAT = {
  can_tall: 'm', tin_flat: 'm', can_wide: 'm', can_ribbed: 'm', candent: 'm',
  drinkcan: 'm', stew_pan: 'm', stew_mug: 'm', tablets: 'm', ammo_r: 'm',
  ammo_p: 'm', watch: 'm', knife: 'm', axe: 'm', rifle: 'm', pistol: 'm',
  spear: 'm', tape: 'm',
  jar_fruit: 'g', bottle_glass: 'g',
  bottle_plastic: 'p', bottle_murky: 'p', pills: 'p', medbox: 'p',
};
const MAT_GRAD = { m: 'ic-sp-m', g: 'ic-sp-g', p: 'ic-sp-p' };

// sh: [cx, rx] of the contact shadow, or null for none
//
// Paint order is the lighting model, in the order light actually arrives:
//   cast shadow -> flat tint body -> material specular -> FORM ramp ->
//   contact occlusion. The specular goes UNDER the form ramp on purpose: the
//   ramp's core shadow then darkens the hotspot wherever the two overlap on the
//   turning-away side, which is the free half of "the highlight must die at the
//   terminator" and costs one mask per symbol instead of two.
const S = (id, body, sh) => {
  const ar = iconAspect(id);
  const vb = ar < 1 ? `${(32 - 32 * ar) / 2} 0 ${32 * ar} 32` : '0 0 32 32';
  const grad = MAT_GRAD[ICON_MAT[id]] || 'ic-sp-c';
  const form = ICON_FORM[id] || 'hard';
  const lay = (g) => `<rect x="0" y="0" width="32" height="32" fill="url(#${g})" mask="url(#ic-mk-${id})"/>`;
  return `<symbol id="ic-${id}" viewBox="${vb}">` +
    `<mask id="ic-mk-${id}" maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">` +
    `<g style="color:#fff">${body}</g></mask>` +
    (sh === null ? ''
      : `<ellipse cx="${sh ? sh[0] : 16}" cy="29.2" rx="${sh ? sh[1] : 9}" ry="2" fill="url(#ic-cast)"/>`) +
    body +
    lay(grad) +
    lay('ic-f-' + form) +
    lay('ic-ao') +
    '</symbol>';
};

export const ITEM_ICON_DEFS = `<svg id="item-icon-defs" style="display:none"
 xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><defs>
 <!-- ============================ FORM RAMPS ============================
      All userSpaceOnUse over the 32x32 authoring canvas: the ramp is anchored
      to the object's own axis, NOT to whatever bbox the silhouette occupies.
      One key direction for the whole sheet (upper left) — what changes per
      class is how the surface turns away from it. See ICON_FORM above.
      White budget: the key stop never exceeds .20 so art cannot climb into
      the ui-hi data tier (theme.js). -->
 <!-- upright cylinder: dark near edge, key band a third across, core shadow
      past centre, bounce/rim on the far edge (the stop that reads as round) -->
 <linearGradient id="ic-f-cyl" gradientUnits="userSpaceOnUse" x1="3" y1="0" x2="29" y2="0">
   <stop offset="0" stop-color="#000000" stop-opacity=".46"/>
   <stop offset=".09" stop-color="#000000" stop-opacity=".17"/>
   <stop offset=".25" stop-color="#ffffff" stop-opacity=".20"/>
   <stop offset=".41" stop-color="#ffffff" stop-opacity=".04"/>
   <stop offset=".58" stop-color="#000000" stop-opacity=".06"/>
   <stop offset=".80" stop-color="#000000" stop-opacity=".30"/>
   <stop offset=".93" stop-color="#000000" stop-opacity=".46"/>
   <stop offset="1" stop-color="#ffffff" stop-opacity=".13"/>
 </linearGradient>
 <!-- the same cylinder lying down -->
 <linearGradient id="ic-f-cylh" gradientUnits="userSpaceOnUse" x1="0" y1="4" x2="0" y2="30">
   <stop offset="0" stop-color="#000000" stop-opacity=".22"/>
   <stop offset=".14" stop-color="#ffffff" stop-opacity=".20"/>
   <stop offset=".36" stop-color="#ffffff" stop-opacity=".04"/>
   <stop offset=".58" stop-color="#000000" stop-opacity=".08"/>
   <stop offset=".84" stop-color="#000000" stop-opacity=".36"/>
   <stop offset=".96" stop-color="#ffffff" stop-opacity=".10"/>
   <stop offset="1" stop-color="#000000" stop-opacity=".28"/>
 </linearGradient>
 <!-- dome/sphere: centre sits OUTSIDE the body at the key position so the
      terminator wraps the volume instead of crossing it -->
 <radialGradient id="ic-f-sph" gradientUnits="userSpaceOnUse" cx="10.5" cy="9.5" r="22">
   <stop offset="0" stop-color="#ffffff" stop-opacity=".20"/>
   <stop offset=".22" stop-color="#ffffff" stop-opacity=".07"/>
   <stop offset=".44" stop-color="#000000" stop-opacity=".03"/>
   <stop offset=".68" stop-color="#000000" stop-opacity=".20"/>
   <stop offset=".88" stop-color="#000000" stop-opacity=".44"/>
   <stop offset=".97" stop-color="#000000" stop-opacity=".30"/>
   <stop offset="1" stop-color="#ffffff" stop-opacity=".10"/>
 </radialGradient>
 <!-- rigid box: two planes with a HARD break where the lit top meets the
      front face. Softening this stop is what turned boxes into pillows. -->
 <linearGradient id="ic-f-box" gradientUnits="userSpaceOnUse" x1="4" y1="0" x2="26" y2="32">
   <stop offset="0" stop-color="#ffffff" stop-opacity=".20"/>
   <stop offset=".30" stop-color="#ffffff" stop-opacity=".10"/>
   <stop offset=".46" stop-color="#ffffff" stop-opacity=".03"/>
   <stop offset=".50" stop-color="#000000" stop-opacity=".12"/>
   <stop offset=".78" stop-color="#000000" stop-opacity=".24"/>
   <stop offset="1" stop-color="#000000" stop-opacity=".42"/>
 </linearGradient>
 <!-- fabric: broad, low contrast, NO rim — cloth has no specular edge -->
 <radialGradient id="ic-f-cloth" gradientUnits="userSpaceOnUse" cx="9" cy="7" r="30">
   <stop offset="0" stop-color="#ffffff" stop-opacity=".16"/>
   <stop offset=".32" stop-color="#ffffff" stop-opacity=".05"/>
   <stop offset=".56" stop-color="#000000" stop-opacity=".03"/>
   <stop offset=".82" stop-color="#000000" stop-opacity=".20"/>
   <stop offset="1" stop-color="#000000" stop-opacity=".36"/>
 </radialGradient>
 <!-- flat hard goods: the old diagonal, anchored in user space and budgeted -->
 <linearGradient id="ic-f-hard" gradientUnits="userSpaceOnUse" x1="3" y1="2" x2="29" y2="30">
   <stop offset="0" stop-color="#ffffff" stop-opacity=".20"/>
   <stop offset=".28" stop-color="#ffffff" stop-opacity=".05"/>
   <stop offset=".52" stop-color="#000000" stop-opacity=".04"/>
   <stop offset=".80" stop-color="#000000" stop-opacity=".26"/>
   <stop offset="1" stop-color="#000000" stop-opacity=".46"/>
 </linearGradient>
 <!-- contact occlusion: every object is standing on the slot plate, so every
      object darkens toward its own base. Shared by all forms. -->
 <linearGradient id="ic-ao" gradientUnits="userSpaceOnUse" x1="0" y1="17" x2="0" y2="30">
   <stop offset="0" stop-color="#000000" stop-opacity="0"/>
   <stop offset="1" stop-color="#000000" stop-opacity=".22"/>
 </linearGradient>
 <radialGradient id="ic-cast" cx=".5" cy=".5" r=".5">
   <stop offset="0" stop-color="#000000" stop-opacity=".55"/>
   <stop offset="1" stop-color="#000000" stop-opacity="0"/>
 </radialGradient>
 <!-- material speculars: same key position as the form ramps (upper left),
      different tightness and strength. m=steel, g=glass, p=plastic,
      c=cloth/organic. Budgeted so tint + form + specular tops out near 215. -->
 <radialGradient id="ic-sp-m" cx=".28" cy=".2" r=".3">
   <stop offset="0" stop-color="#ffffff" stop-opacity=".22"/>
   <stop offset=".55" stop-color="#ffffff" stop-opacity=".06"/>
   <stop offset="1" stop-color="#ffffff" stop-opacity="0"/>
 </radialGradient>
 <radialGradient id="ic-sp-g" cx=".26" cy=".18" r=".24">
   <stop offset="0" stop-color="#ffffff" stop-opacity=".30"/>
   <stop offset=".5" stop-color="#ffffff" stop-opacity=".08"/>
   <stop offset="1" stop-color="#ffffff" stop-opacity="0"/>
 </radialGradient>
 <radialGradient id="ic-sp-p" cx=".28" cy=".22" r=".4">
   <stop offset="0" stop-color="#ffffff" stop-opacity=".18"/>
   <stop offset="1" stop-color="#ffffff" stop-opacity="0"/>
 </radialGradient>
 <radialGradient id="ic-sp-c" cx=".3" cy=".24" r=".62">
   <stop offset="0" stop-color="#ffffff" stop-opacity=".09"/>
   <stop offset="1" stop-color="#ffffff" stop-opacity="0"/>
 </radialGradient>` + [
  // =========================================================================
  // TINNED FOOD — five different tins, not five tints of one tin
  // =========================================================================
  // stew: standard tall food can, paper label band
  S('can_tall', B(`<path fill="{f}" d="M9 8.4h14v16.6a2.6 2.6 0 0 1-2.6 2.6h-8.8A2.6 2.6 0 0 1 9 25z"/>`) +
    `<ellipse cx="16" cy="8.4" rx="7" ry="2.5" fill="currentColor"/>
     <ellipse cx="16" cy="8.4" rx="7" ry="2.5" fill="${L2}"/>
     <ellipse cx="16" cy="8.2" rx="4.4" ry="1.5" fill="${D}"/>
     <rect x="9" y="10.8" width="14" height="1" fill="${D}"/>
     <rect x="9" y="23.2" width="14" height="1" fill="${D}"/>
     <rect x="9" y="13.2" width="14" height="8.4" fill="rgba(198,193,178,.86)"/>
     <rect x="9" y="15.4" width="14" height="2.2" fill="rgba(0,0,0,.5)"/>
     <rect x="11.2" y="18.6" width="7" height="1.4" fill="rgba(0,0,0,.34)"/>
     <rect x="10.6" y="9" width="1.5" height="15" fill="${L}"/>`, [16, 8]),
  // fish: flat oval sardine tin with a pull ring — a WIDE, SHORT silhouette
  S('tin_flat', B(`<path fill="{f}" d="M6 13.6h20a3.6 3.6 0 0 1 3.6 3.6v3.6a3.6 3.6 0 0 1-3.6 3.6H6a3.6 3.6 0 0 1-3.6-3.6v-3.6A3.6 3.6 0 0 1 6 13.6z"/>`) +
    `<path d="M6.4 15h19.2a2.4 2.4 0 0 1 2.4 2.4v2.6a2.4 2.4 0 0 1-2.4 2.4H6.4A2.4 2.4 0 0 1 4 20v-2.6A2.4 2.4 0 0 1 6.4 15z" fill="${D}" opacity=".55"/>
     <circle cx="24.4" cy="18.6" r="2.6" fill="none" stroke="${L2}" stroke-width="1.3"/>
     <path d="M8.4 18.6c1.8-2 4.2-3 6.4-3s3.8 1 4.8 3c-1 2-2.6 3-4.8 3s-4.6-1-6.4-3z" fill="rgba(0,0,0,.45)"/>
     <path d="M8.4 18.6l-2.6-2.2v4.4z" fill="rgba(0,0,0,.45)"/>
     <rect x="4.4" y="15.4" width="21" height="1" fill="${L}"/>`, [16, 12]),
  // veg: short wide can, ring-pull lid, vertical stripe label
  S('can_wide', B(`<path fill="{f}" d="M6.4 13.8h19.2v9.4a3 3 0 0 1-3 3H9.4a3 3 0 0 1-3-3z"/>`) +
    `<ellipse cx="16" cy="13.8" rx="9.6" ry="3" fill="currentColor"/>
     <ellipse cx="16" cy="13.8" rx="9.6" ry="3" fill="${L2}"/>
     <ellipse cx="16" cy="13.6" rx="6.2" ry="1.8" fill="${D}"/>
     <path d="M12.6 13.4h6.4" stroke="${L2}" stroke-width="1.4"/>
     <g fill="${D}"><rect x="8.6" y="17" width="2.4" height="7.6"/><rect x="14.8" y="17" width="2.4" height="7.6"/>
       <rect x="21" y="17" width="2.4" height="7.6"/></g>
     <rect x="6.4" y="16.2" width="19.2" height="1" fill="${L}"/>`, [16, 10.5]),
  // soup: tall NARROW ribbed can
  S('can_ribbed', B(`<path fill="{f}" d="M11 6.6h10v18.4a2.4 2.4 0 0 1-2.4 2.4h-5.2A2.4 2.4 0 0 1 11 25z"/>`) +
    `<ellipse cx="16" cy="6.6" rx="5" ry="1.9" fill="currentColor"/>
     <ellipse cx="16" cy="6.6" rx="5" ry="1.9" fill="${L2}"/>
     <ellipse cx="16" cy="6.5" rx="3.1" ry="1.1" fill="${D}"/>
     <g fill="${D}"><rect x="11" y="10.4" width="10" height="1.8"/><rect x="11" y="13.6" width="10" height="1.8"/>
       <rect x="11" y="16.8" width="10" height="1.8"/><rect x="11" y="20" width="10" height="1.8"/>
       <rect x="11" y="23.2" width="10" height="1.8"/></g>
     <rect x="12.2" y="7.6" width="1.3" height="18" fill="${L}"/>`, [16, 6.4]),
  // fruit: shouldered glass preserve jar with a screw lid and visible chunks
  S('jar_fruit', `<rect x="11.4" y="2.6" width="9.2" height="4" rx=".8" fill="${D2}"/>
    <rect x="11.4" y="2.6" width="9.2" height="1.4" fill="${L}"/>` +
    B(`<path fill="{f}" d="M12.2 6.6h7.6v2.4l3.5 3.4a4.4 4.4 0 0 1 1.3 3.1v9.6a2.6 2.6 0 0 1-2.6 2.6H10a2.6 2.6 0 0 1-2.6-2.6v-9.6a4.4 4.4 0 0 1 1.3-3.1l3.5-3.4z"/>`) +
    `<path d="M7.4 16.6h17.2v8.5a2.6 2.6 0 0 1-2.6 2.6H10a2.6 2.6 0 0 1-2.6-2.6z" fill="${D}"/>
     <g fill="${L2}"><circle cx="12" cy="20" r="2.4"/><circle cx="19.6" cy="19.2" r="2.1"/>
       <circle cx="16" cy="24" r="2.3"/></g>
     <rect x="9.6" y="12" width="1.6" height="14" rx=".8" fill="${L2}"/>`, [16, 9]),
  // bulging: domed, swollen lid + rust — the "do not eat" tin
  S('candent', B(`<path fill="{f}" d="M9.4 10h13.2v15a2.6 2.6 0 0 1-2.6 2.6h-8A2.6 2.6 0 0 1 9.4 25z"/>`) +
    B(`<path fill="{f}" d="M9.4 10c0-4.3 2.9-6.9 6.6-6.9s6.6 2.6 6.6 6.9c0 1.5-3 2.4-6.6 2.4S9.4 11.5 9.4 10z"/>`) +
    `<rect x="9.4" y="13.6" width="13.2" height="7.4" fill="${D}"/>
     <g fill="rgba(120,72,40,.7)"><path d="M11 14.6c1.6 2 1.2 4.4 0 6.4h-1.6v-6.4z"/>
       <circle cx="19.6" cy="23.6" r="1.7"/><circle cx="17" cy="25.4" r="1.1"/></g>
     <path d="M9.6 8.6c2.6 1.6 10.2 1.6 12.8 0" stroke="${L}" stroke-width="1.2" fill="none"/>`, [16, 8]),

  // =========================================================================
  // DRY FOOD / DRINK
  // =========================================================================
  S('crackers', B(`<path fill="{f}" d="M8.6 4.8h14.8v22.4H8.6z"/>`) +
    `<path d="M8.6 4.8h14.8v3.6H8.6zM8.6 23.6h14.8v3.6H8.6z" fill="${D}"/>
     <rect x="10.6" y="10.6" width="10.8" height="10.8" rx="1" fill="rgba(198,193,178,.9)"/>
     <g fill="rgba(0,0,0,.4)"><circle cx="14" cy="14" r=".9"/><circle cx="18" cy="14" r=".9"/>
       <circle cx="14" cy="18" r=".9"/><circle cx="18" cy="18" r=".9"/><circle cx="16" cy="16" r=".9"/></g>
     <rect x="9.4" y="5.4" width="1.6" height="21" fill="${L}"/>`, [16, 8]),
  S('candy', B(`<path fill="{f}" d="M5 11.6h22v8.8H5zM5 11.6l-2.4-2.2v13.2L5 20.4zM27 11.6l2.4-2.2v13.2L27 20.4z"/>`) +
    `<rect x="10.6" y="11.6" width="3.6" height="8.8" fill="${D}"/>
     <rect x="17.8" y="11.6" width="3.6" height="8.8" fill="${D}"/>
     <rect x="5" y="12.6" width="22" height="1.6" fill="${L2}"/>
     <rect x="5" y="19" width="22" height="1" fill="${D}"/>`, [16, 11]),
  S('drinkcan', B(`<path fill="{f}" d="M11 7h10v18.4a2.6 2.6 0 0 1-2.6 2.6h-4.8A2.6 2.6 0 0 1 11 25.4z"/>`) +
    `<ellipse cx="16" cy="7" rx="5" ry="1.9" fill="${STEEL}"/>
     <ellipse cx="16" cy="6.9" rx="3.2" ry="1.1" fill="${D2}"/>
     <rect x="14.7" y="5.6" width="2.6" height="1.3" rx=".6" fill="${D2}"/>
     <path d="M11 12.4h10v7.6H11z" fill="${D}"/>
     <path d="M11.6 14.2c2.6 1.6 6.2 1.6 8.8 0" stroke="${L2}" stroke-width="1.3" fill="none"/>
     <rect x="12.2" y="8" width="1.4" height="17" fill="${L}"/>`, [16, 6.4]),
  // vodka: shouldered GLASS bottle, foil neck, paper label
  S('bottle_glass', `<rect x="13.2" y="2.2" width="5.6" height="4" rx=".7" fill="${D2}"/>` +
    B(`<path fill="{f}" d="M13.4 5.8h5.2v3.6l2.8 3.4a5 5 0 0 1 1.2 3.2v10.2a2.4 2.4 0 0 1-2.4 2.4h-8.4a2.4 2.4 0 0 1-2.4-2.4V16a5 5 0 0 1 1.2-3.2l2.8-3.4z"/>`) +
    `<rect x="9.6" y="17" width="12.8" height="7.4" fill="rgba(196,191,174,.88)"/>
     <rect x="11.2" y="19" width="9.6" height="1.6" fill="rgba(0,0,0,.45)"/>
     <rect x="11.2" y="21.6" width="6.4" height="1.1" fill="rgba(0,0,0,.3)"/>
     <rect x="11" y="12" width="1.7" height="14" rx=".85" fill="${L2}"/>`, [16, 7.5]),
  // water: ribbed PLASTIC bottle, wide cap
  S('bottle_plastic', `<rect x="12.6" y="2.4" width="6.8" height="3.4" rx=".8" fill="${D2}"/>
    <rect x="13" y="5.8" width="6" height="1.4" fill="${D}"/>` +
    B(`<path fill="{f}" d="M13 7.2h6v2.4l2.6 2.6a5 5 0 0 1 1.4 3.4v10.2a2.4 2.4 0 0 1-2.4 2.4h-8.2a2.4 2.4 0 0 1-2.4-2.4V15.6a5 5 0 0 1 1.4-3.4l2.6-2.6z"/>`) +
    `<g fill="${D}"><rect x="9.4" y="17.4" width="13.2" height="1.5"/><rect x="9.4" y="20.4" width="13.2" height="1.5"/>
      <rect x="9.4" y="23.4" width="13.2" height="1.5"/></g>
     <rect x="10.8" y="12" width="1.6" height="14" rx=".8" fill="${L2}"/>`, [16, 7.5]),
  // murky: same plastic bottle, no label, heavy sediment in the bottom third
  S('bottle_murky', `<rect x="12.6" y="2.4" width="6.8" height="3.4" rx=".8" fill="${D2}"/>` +
    B(`<path fill="{f}" d="M13 6.4h6v3l2.6 2.6a5 5 0 0 1 1.4 3.4v10.2a2.4 2.4 0 0 1-2.4 2.4h-8.2a2.4 2.4 0 0 1-2.4-2.4V15.4a5 5 0 0 1 1.4-3.4L13 9.4z"/>`) +
    `<path d="M9.4 20.6h13.2v5a2.4 2.4 0 0 1-2.4 2.4h-8.4a2.4 2.4 0 0 1-2.4-2.4z" fill="rgba(38,32,20,.75)"/>
     <g fill="rgba(20,16,10,.6)"><circle cx="12.4" cy="18.6" r="1.5"/><circle cx="18.8" cy="17.4" r="1.1"/>
       <circle cx="16" cy="19.8" r=".9"/></g>
     <rect x="10.8" y="12" width="1.4" height="8" rx=".7" fill="${L}"/>`, [16, 7.5]),

  // =========================================================================
  // MUSHROOMS — ten cap profiles. Stems are shared bone; the CAP identifies.
  // =========================================================================
  // porcini: thick low dome on a fat bulbous stem
  S('mu_porcini', `<path d="M11.4 16h9.2c.8 3.4 1.4 6 1.4 8 0 2.4-2.6 3.6-6 3.6s-6-1.2-6-3.6c0-2 .6-4.6 1.4-8z" fill="${ST}"/>
    <path d="M17.8 16h2.8c.8 3.4 1.4 6 1.4 8 0 1.7-1.3 2.8-3.4 3.3 .8-3.6 .5-7.6-.8-11.3z" fill="${ST2}"/>` +
    B(`<path fill="{f}" d="M3.2 16.4C3.2 10.2 8.9 5.6 16 5.6s12.8 4.6 12.8 10.8c0 1.7-1.2 2.6-3.1 2.6H6.3c-1.9 0-3.1-.9-3.1-2.6z"/>`) +
    `<ellipse cx="10.6" cy="11" rx="3.4" ry="1.9" fill="${L}"/>
     <path d="M3.6 17.8h24.8" stroke="${D}" stroke-width="1.1"/>`, [16, 8]),
  // chanterelle: funnel/trumpet, wavy rim, gills running down the stem
  S('mu_chant', `<path d="M13 12.6h6l-1.3 12.4c-.2 1.9-.7 2.8-1.7 2.8s-1.5-.9-1.7-2.8z" fill="currentColor"/>
    <path d="M16.6 12.6h2.4l-1.3 12.4c-.2 1.9-.7 2.8-1.7 2.8-.3 0-.6-.1-.8-.3 1-1 1.3-9.6 1.4-14.9z" fill="${ST2}"/>` +
    B(`<path fill="{f}" d="M3.8 12.2C6.4 8.6 10.8 6.6 16 6.6s9.6 2 12.2 5.6c-2.4 2.6-4.4.6-6.2 2.6s-3.4-.2-4.8 2c-1.4-2.2-3-.6-4.8-2S6.2 14.8 3.8 12.2z"/>`) +
    `<g stroke="${D}" stroke-width=".9" fill="none">
      <path d="M9.4 12.4c2.4 2.6 4 6 4.6 10"/><path d="M22.6 12.4c-2.4 2.6-4 6-4.6 10"/>
      <path d="M16 10.6v12"/></g>
     <path d="M6.6 10.4c2.4-1.8 5.6-2.8 9.4-2.8" stroke="${L2}" stroke-width="1.4" fill="none"/>`, [16, 7]),
  // birch bolete: hemispherical cap, TALL slender scabered stem
  S('mu_bolete', `<path d="M13.4 14.6h5.2l1.1 11c.2 1.7-1.3 2.7-3.7 2.7s-3.9-1-3.7-2.7z" fill="${ST}"/>
    <g fill="rgba(40,34,26,.65)"><rect x="14" y="17.4" width="2.2" height=".9"/><rect x="17.2" y="19.4" width="2" height=".9"/>
      <rect x="13.6" y="21.4" width="2.4" height=".9"/><rect x="17" y="23.4" width="2.2" height=".9"/>
      <rect x="14.2" y="25.2" width="2" height=".9"/></g>` +
    B(`<path fill="{f}" d="M5.4 13.8C5.4 8.4 10.1 4.2 16 4.2s10.6 4.2 10.6 9.6c0 1.6-1.1 2.4-2.8 2.4H8.2c-1.7 0-2.8-.8-2.8-2.4z"/>`) +
    `<ellipse cx="11.4" cy="9.2" rx="3.2" ry="2" fill="${L}"/>
     <path d="M5.8 15h20.4" stroke="${D}" stroke-width="1"/>`, [16, 6]),
  // puffball: a plain warty SPHERE — no cap/stem division at all
  S('mu_puff', B(`<circle fill="{f}" cx="16" cy="16.6" r="10.2"/>`) +
    `<path d="M13.4 26.4h5.2l.5 1.4c-1 .3-2 .4-3.1.4s-2.1-.1-3.1-.4z" fill="${ST2}"/>
     <g fill="${D}" opacity=".55"><circle cx="12" cy="12.4" r="1"/><circle cx="18.6" cy="10.6" r=".9"/>
       <circle cx="21.4" cy="15.6" r="1"/><circle cx="15.4" cy="17.4" r=".9"/><circle cx="11.4" cy="19.6" r="1"/>
       <circle cx="19" cy="21.4" r=".9"/><circle cx="24" cy="20" r=".8"/></g>
     <ellipse cx="11.6" cy="11.4" rx="3.4" ry="2.4" fill="${L}" transform="rotate(-32 11.6 11.4)"/>`, [16, 9.5]),
  // morel: ovoid HONEYCOMB cap — a grid of deep pits
  S('mu_morel', `<path d="M12.8 20.8h6.4v4.2c0 1.9-1.2 3-3.2 3s-3.2-1.1-3.2-3z" fill="${ST}"/>` +
    B(`<path fill="{f}" d="M16 3.2c4.6 0 7.6 4.2 7.6 9.8s-3 9.4-7.6 9.4-7.6-3.8-7.6-9.4S11.4 3.2 16 3.2z"/>`) +
    `<g fill="${D2}"><rect x="10.8" y="6.4" width="3" height="2.4" rx=".9"/><rect x="14.5" y="5.2" width="3" height="2.4" rx=".9"/>
      <rect x="18.2" y="6.6" width="2.8" height="2.4" rx=".9"/>
      <rect x="9.6" y="10" width="3" height="2.6" rx="1"/><rect x="13.4" y="9.4" width="3.2" height="2.6" rx="1"/>
      <rect x="17.4" y="10.2" width="3" height="2.6" rx="1"/>
      <rect x="10.2" y="13.8" width="3" height="2.6" rx="1"/><rect x="14" y="13.4" width="3.2" height="2.6" rx="1"/>
      <rect x="18" y="14" width="2.8" height="2.6" rx="1"/>
      <rect x="11.4" y="17.6" width="3" height="2.4" rx=".9"/><rect x="15.4" y="17.4" width="3" height="2.4" rx=".9"/></g>
     <path d="M11 7.4c-1 2-1.6 4.2-1.6 6.4" stroke="${L}" stroke-width="1.4" fill="none"/>`, [16, 6.4]),
  S('mu_morel_roast', SKEWER +
    `<path d="M12.8 20.8h6.4v4.2c0 1.9-1.2 3-3.2 3s-3.2-1.1-3.2-3z" fill="rgba(150,132,98,.95)"/>` +
    B(`<path fill="{f}" d="M16 3.2c4.6 0 7.6 4.2 7.6 9.8s-3 9.4-7.6 9.4-7.6-3.8-7.6-9.4S11.4 3.2 16 3.2z"/>`) +
    `<g fill="${D2}"><rect x="10.8" y="6.4" width="3" height="2.4" rx=".9"/><rect x="14.5" y="5.2" width="3" height="2.4" rx=".9"/>
      <rect x="18.2" y="6.6" width="2.8" height="2.4" rx=".9"/><rect x="9.6" y="10" width="3" height="2.6" rx="1"/>
      <rect x="13.4" y="9.4" width="3.2" height="2.6" rx="1"/><rect x="17.4" y="10.2" width="3" height="2.6" rx="1"/>
      <rect x="10.2" y="13.8" width="3" height="2.6" rx="1"/><rect x="14" y="13.4" width="3.2" height="2.6" rx="1"/>
      <rect x="18" y="14" width="2.8" height="2.6" rx="1"/></g>` +
    CHAR('M9.6 16.6c2.4 1.6 5.2 2.2 8.4 1.8M10.8 19.4c2 1.2 4.2 1.6 6.6 1.4'), [16, 6.4]),
  // fly agaric: broad domed cap with WHITE WARTS and a stem ring
  S('mu_agaric', `<path d="M13.2 15.8h5.6l.9 9.6c.2 1.8-1.4 2.8-3.7 2.8s-3.9-1-3.7-2.8z" fill="${ST}"/>
    <path d="M11.6 19.2h8.8c.6 0 1 .4 1 1s-.4 1-1 1h-8.8c-.6 0-1-.4-1-1s.4-1 1-1z" fill="rgba(196,189,170,.95)"/>` +
    B(`<path fill="{f}" d="M3.8 15.2C3.8 9.4 9.2 5 16 5s12.2 4.4 12.2 10.2c0 1.6-1.2 2.4-2.9 2.4H6.7c-1.7 0-2.9-.8-2.9-2.4z"/>`) +
    `<g fill="rgba(200,197,185,.94)"><circle cx="9.6" cy="12.6" r="1.7"/><circle cx="14.6" cy="9.4" r="1.9"/>
      <circle cx="20.4" cy="11.4" r="1.7"/><circle cx="24.4" cy="14.6" r="1.4"/><circle cx="16.6" cy="14.6" r="1.5"/>
      <circle cx="6.6" cy="15.4" r="1.2"/></g>
     <path d="M4.2 16.6h23.6" stroke="${D}" stroke-width="1.1"/>`, [16, 8]),
  // death cap: smooth pale cap, drooping skirt ring AND a bulbous volva sac
  S('mu_death', `<path d="M14 14.8h4l.6 8.6h-5.2z" fill="${ST}"/>
    <path d="M11.6 23c0-1.7 2-2.8 4.4-2.8s4.4 1.1 4.4 2.8c0 2.6-2 4.6-4.4 4.6s-4.4-2-4.4-4.6z" fill="${ST}"/>
    <path d="M11.6 23c0-1.7 2-2.8 4.4-2.8 1 0 2 .2 2.8.5-1.6.5-2.6 1.4-2.6 2.6 0 1.8 1 3.4 2.6 4.2-.9.1-1.6.1-2.8.1-2.4 0-4.4-2-4.4-4.6z" fill="${ST2}"/>
    <path d="M11 15.6h10c.7 0 1.2.5 1.2 1.2 0 1.4-2.8 2.2-6.2 2.2s-6.2-.8-6.2-2.2c0-.7.5-1.2 1.2-1.2z" fill="rgba(196,191,174,.95)"/>` +
    B(`<path fill="{f}" d="M4.6 14.2C4.6 8.9 9.7 4.6 16 4.6s11.4 4.3 11.4 9.6c0 1.6-1.2 2.4-3 2.4H7.6c-1.8 0-3-.8-3-2.4z"/>`) +
    `<ellipse cx="11" cy="9.6" rx="3.6" ry="2.2" fill="${L}"/>
     <path d="M5 15.4h22" stroke="${D}" stroke-width="1"/>`, [16, 6.6]),
  // false morel: BRAIN-lobed, wrinkled cap on a thick stem
  S('mu_false', `<path d="M12.6 18.6h6.8v6c0 2-1.4 3.2-3.4 3.2s-3.4-1.2-3.4-3.2z" fill="${ST}"/>` +
    B(`<path fill="{f}" d="M6 13.6c0-3.4 2.3-5.6 5-5.3 1-2.6 3-4.1 5.4-3.9 2.4.2 4.1 1.8 4.7 4 2.6-.6 5 1.4 5 4.4 0 1.7-.8 3.1-2 3.9.4 1.9-.9 3.7-3.1 3.9-1.1 1.4-2.9 2.1-4.7 1.9-1.8.2-3.6-.5-4.7-1.9-2.2-.2-3.5-2-3.1-3.9-1.3-.8-2.5-2.2-2.5-3.1z"/>`) +
    `<g stroke="${D2}" stroke-width="1.3" fill="none" stroke-linecap="round">
      <path d="M9.4 10.4c2 1.4 2.6 3.6 1.8 5.8"/><path d="M15.6 6c-1 2.4-.6 5 1 7.2"/>
      <path d="M21.6 9.6c-1.6 1.6-2 4-1 6.2"/><path d="M12 18c2.6 1.4 5.4 1.4 8 0"/></g>
     <ellipse cx="11.6" cy="9" rx="2.4" ry="1.5" fill="${L}" transform="rotate(-28 11.6 9)"/>`, [16, 7.5]),
  S('mu_false_roast', SKEWER +
    `<path d="M12.6 18.6h6.8v6c0 2-1.4 3.2-3.4 3.2s-3.4-1.2-3.4-3.2z" fill="rgba(150,132,98,.95)"/>` +
    B(`<path fill="{f}" d="M6 13.6c0-3.4 2.3-5.6 5-5.3 1-2.6 3-4.1 5.4-3.9 2.4.2 4.1 1.8 4.7 4 2.6-.6 5 1.4 5 4.4 0 1.7-.8 3.1-2 3.9.4 1.9-.9 3.7-3.1 3.9-1.1 1.4-2.9 2.1-4.7 1.9-1.8.2-3.6-.5-4.7-1.9-2.2-.2-3.5-2-3.1-3.9-1.3-.8-2.5-2.2-2.5-3.1z"/>`) +
    CHAR('M8.4 12.6c2.6 2 6 2.8 9.6 2.4M10 16.4c2.6 1.6 5.4 2 8.4 1.2'), [16, 7.5]),

  // =========================================================================
  // BERRIES — eight clusters. Count, arrangement and stem carry identity.
  // =========================================================================
  // blueberry: three round berries, each with the calyx star
  S('be_blue', `<path d="M17.4 8.6c-.6-3-2.8-5-5.8-5.6.4 3 2.4 5 5.2 5.8z" fill="${GRN}"/>
    <path d="M16.6 9.4V4.4" stroke="rgba(70,90,44,.9)" stroke-width="1.4" stroke-linecap="round"/>` +
    B(`<g fill="{f}"><circle cx="10.8" cy="20" r="5.4"/><circle cx="21.2" cy="20" r="5.4"/><circle cx="16" cy="12.6" r="5.2"/></g>`) +
    // The blossom end of a blueberry is a RECESSED five-lobed calyx, not a
    // sticker. Round 6 drew a filled five-point star on each drupe and the
    // critique read it, correctly, as cartoon art: a solid convex star is the
    // one shape that cannot be a dimple. It is now a sunken well with five
    // short radial creases and a hairline catching the key on its upper lip.
    CALYX(10.8, 19.6, 2) + CALYX(21.2, 19.6, 2) + CALYX(16, 12.2, 1.95) +
    `<ellipse cx="12.9" cy="10.2" rx="1.5" ry="1" transform="rotate(-34 12.9 10.2)" fill="${L2}"/>`,
    [16, 10]),
  // raspberry: an aggregate CONE of drupelets under a green sepal crown
  S('be_rasp', `<g fill="${GRN}"><path d="M16 6.6L9.6 4.2c-.4 2 .6 3.6 2.4 4.2z"/>
      <path d="M16 6.6l6.4-2.4c.4 2-.6 3.6-2.4 4.2z"/><path d="M16 6.6L16 2.6c1.6.6 2.4 2.2 2 4z"/></g>` +
    B(`<g fill="{f}"><circle cx="10" cy="11.4" r="2.9"/><circle cx="15.4" cy="10.6" r="2.9"/><circle cx="20.8" cy="11.4" r="2.9"/>
      <circle cx="12.4" cy="15.8" r="2.9"/><circle cx="18" cy="15.8" r="2.9"/><circle cx="23" cy="15.4" r="2.5"/>
      <circle cx="8.4" cy="15.6" r="2.4"/><circle cx="14.2" cy="20" r="2.8"/><circle cx="19.6" cy="20" r="2.7"/>
      <circle cx="10.6" cy="19.6" r="2.3"/><circle cx="16.6" cy="24" r="2.6"/><circle cx="12.4" cy="23.6" r="2.2"/></g>`) +
    `<g fill="${L2}"><circle cx="9.2" cy="10.4" r=".9"/><circle cx="14.6" cy="9.6" r=".9"/><circle cx="11.6" cy="14.8" r=".8"/></g>`,
  [16, 10]),
  // lingonberry: a PAIR hanging from an arched stem with small oval leaves
  S('be_lingon', `<path d="M16 26V9c0-3.4 2.6-5.6 6.4-5.6" stroke="rgba(90,104,58,.95)" stroke-width="1.8" fill="none" stroke-linecap="round"/>
    <path d="M11.4 15.6V9" stroke="rgba(90,104,58,.95)" stroke-width="1.6" fill="none" stroke-linecap="round"/>
    <g fill="${GRN}"><ellipse cx="21.4" cy="12" rx="4.4" ry="2.4" transform="rotate(28 21.4 12)"/>
      <ellipse cx="8.4" cy="8.4" rx="4" ry="2.2" transform="rotate(-24 8.4 8.4)"/></g>` +
    B(`<g fill="{f}"><circle cx="16" cy="21.6" r="6"/><circle cx="11.4" cy="18.4" r="4.2"/></g>`) +
    `<g fill="${D2}"><circle cx="16" cy="26.6" r="1.1"/><circle cx="11.4" cy="22.1" r=".8"/></g>
     <circle cx="13.6" cy="18.4" r="1.6" fill="${L2}"/>`, [16, 8]),
  // elderberry: a flat-topped UMBEL — many tiny berries on radiating stalks
  S('be_elder', `<g stroke="rgba(96,72,66,.95)" stroke-width="1.2" fill="none" stroke-linecap="round">
      <path d="M16 27.4V17"/><path d="M16 18.6L6.4 12.4M16 18.6l9.6-6.2M16 18.6l-5-8.4M16 18.6l5-8.4M16 18.6V9.6"/></g>` +
    B(`<g fill="{f}"><circle cx="6" cy="11.6" r="2.7"/><circle cx="10.4" cy="9.4" r="2.7"/><circle cx="16" cy="8.6" r="2.9"/>
      <circle cx="21.4" cy="9.4" r="2.7"/><circle cx="26" cy="11.6" r="2.7"/><circle cx="8" cy="14.8" r="2.4"/>
      <circle cx="13.2" cy="12.6" r="2.6"/><circle cx="18.8" cy="12.6" r="2.6"/><circle cx="24" cy="14.8" r="2.4"/>
      <circle cx="11.4" cy="16.2" r="2.3"/><circle cx="16" cy="14.4" r="2.5"/><circle cx="20.6" cy="16.2" r="2.3"/></g>`) +
    `<g fill="${L2}"><circle cx="9.4" cy="8.4" r=".8"/><circle cx="15" cy="7.6" r=".9"/><circle cx="12.2" cy="11.6" r=".7"/></g>`,
  [16, 9]),
  // rosehip: ONE big hip with the dried sepal crown at the bottom + thorny stem
  S('be_rose', `<path d="M16 8V2.6" stroke="rgba(96,110,60,.95)" stroke-width="1.8" stroke-linecap="round"/>
    <g fill="rgba(96,110,60,.95)"><path d="M16.4 5.4l3.4-2 .4 2.6z"/><path d="M15.6 3.6l-3-1.4-.2 2.4z"/></g>` +
    B(`<path fill="{f}" d="M16 6.4c4.2 0 7.2 3.8 7.2 8.8 0 5.6-3.2 9.4-7.2 9.4s-7.2-3.8-7.2-9.4c0-5 3-8.8 7.2-8.8z"/>`) +
    `<g fill="${D2}"><path d="M16 22.6l1.6 3.4-3.2 0zM16 23l4-1.6-1.6 3.6zM16 23l-4-1.6 1.6 3.6z"/></g>
     <ellipse cx="12.6" cy="11.4" rx="2.2" ry="3.2" fill="${L2}" transform="rotate(-22 12.6 11.4)"/>`, [16, 7.5]),
  // rowan: a dense DROOPING bunch with a pinnate leaf beside it
  S('be_rowan', `<path d="M16 4.4c-2.4 2-4.2 4.4-5.4 7.2M16 4.4c2.4 2 4.2 4.4 5.4 7.2" stroke="rgba(96,72,66,.9)" stroke-width="1.2" fill="none"/>
    <g fill="${GRN}"><ellipse cx="5.6" cy="8.6" rx="3.4" ry="1.7" transform="rotate(-22 5.6 8.6)"/>
      <ellipse cx="7.4" cy="13" rx="3.4" ry="1.7" transform="rotate(-14 7.4 13)"/>
      <ellipse cx="26.4" cy="9" rx="3.2" ry="1.6" transform="rotate(22 26.4 9)"/></g>` +
    B(`<g fill="{f}"><circle cx="16" cy="10.6" r="3"/><circle cx="11.4" cy="14" r="3.1"/><circle cx="20.6" cy="14" r="3.1"/>
      <circle cx="16" cy="16.4" r="3.2"/><circle cx="9.6" cy="19.6" r="3"/><circle cx="22.4" cy="19.6" r="3"/>
      <circle cx="16" cy="22" r="3.2"/><circle cx="12" cy="24.6" r="2.7"/><circle cx="20" cy="24.6" r="2.7"/></g>`) +
    `<g fill="${L2}"><circle cx="15" cy="9.4" r=".9"/><circle cx="10.4" cy="12.8" r=".9"/><circle cx="8.6" cy="18.4" r=".8"/></g>`,
  [16, 10]),
  // stewed elderberry: a shallow PAN of dark stew, steam rising
  S('stew_pan', `<g stroke="rgba(226,226,220,.4)" stroke-width="1.3" fill="none" stroke-linecap="round">
      <path d="M12.4 8.4c1.4-1.4-.8-2.6.6-4"/><path d="M18.6 8.4c1.4-1.4-.8-2.6.6-4"/></g>
    <path d="M23.6 16.6h5.6c.9 0 1.6.7 1.6 1.6s-.7 1.6-1.6 1.6h-5.6z" fill="rgba(58,54,50,.95)"/>` +
    B(`<path fill="{f}" d="M3.4 14.4h21.2v4.2a7 7 0 0 1-7 7h-7.2a7 7 0 0 1-7-7z"/>`) +
    `<ellipse cx="14" cy="14.4" rx="10.6" ry="2.6" fill="rgba(52,30,44,.95)"/>
     <g fill="rgba(84,50,72,.95)"><circle cx="10" cy="14" r="1.7"/><circle cx="15" cy="14.8" r="1.5"/>
       <circle cx="19" cy="13.8" r="1.4"/></g>
     <rect x="4" y="16.6" width="20" height="1.2" fill="${L}"/>`, [14, 11]),
  // stewed rowan: a tin MUG heaped with berries, steam rising
  S('stew_mug', `<g stroke="rgba(226,226,220,.4)" stroke-width="1.3" fill="none" stroke-linecap="round">
      <path d="M12.4 6.4c1.4-1.4-.8-2.6.6-4"/><path d="M18.6 6.4c1.4-1.4-.8-2.6.6-4"/></g>
    <path d="M21.6 14.4h2.6a4 4 0 0 1 0 8h-2.6v-2.6h2.2a1.4 1.4 0 0 0 0-2.8h-2.2z" fill="${STEEL}"/>` +
    B(`<path fill="{f}" d="M7 11.6h15v11.6a4.4 4.4 0 0 1-4.4 4.4h-6.2A4.4 4.4 0 0 1 7 23.2z"/>`) +
    `<ellipse cx="14.5" cy="11.6" rx="7.5" ry="2.2" fill="rgba(40,34,28,.9)"/>
     <g fill="currentColor"><circle cx="11" cy="10.6" r="2.3"/><circle cx="15.6" cy="9.8" r="2.5"/><circle cx="19.4" cy="11" r="2.1"/>
       <circle cx="13.4" cy="8" r="2.2"/></g>
     <g fill="${D}" opacity=".5"><circle cx="11" cy="10.6" r="2.3"/><circle cx="19.4" cy="11" r="2.1"/></g>
     <rect x="8.4" y="13.4" width="1.6" height="12" fill="${L}"/>`, [14.5, 8.5]),
  S('apple', `<path d="M16.4 9.2c0-3.2 1.8-5.2 4.6-5.8-.2 3.2-1.9 5.2-4.6 5.8z" fill="${GRN}"/>
    <path d="M15.6 9.2V4.6" stroke="rgba(74,56,36,.95)" stroke-width="1.6" stroke-linecap="round"/>` +
    B(`<path fill="{f}" d="M16 9.6c2.2-2 6-1.9 8 .8 2.4 3.2 1.4 9.4-1.6 13.4-1.6 2.1-3.2 3.3-4.6 3.3-1 0-1.4-.5-1.8-.5s-.8.5-1.8.5c-1.4 0-3-1.2-4.6-3.3-3-4-4-10.2-1.6-13.4 2-2.7 5.8-2.8 8-.8z"/>`) +
    `<ellipse cx="11.8" cy="14" rx="1.8" ry="3.4" fill="${L2}" transform="rotate(-18 11.8 14)"/>`, [16, 8.5]),

  // =========================================================================
  // MEAT — three cuts, each raw and roasted
  // =========================================================================
  S('cut_drum', B(`<path fill="{f}" d="M9.6 6.4c4.6 0 8.4 3.6 8.4 8.4 0 3.4-1.6 5.6-3.6 7.2l-3.4 2.6c-2.6 2-6.6.4-6.6-3 0-1.6.6-2.6.6-4.4C5 12 5.6 6.4 9.6 6.4z"/>`) +
    `<path d="M16.6 19.4l3.4 3.4a3.4 3.4 0 1 1-4.8 4.8l-3.4-3.4z" fill="${ST}"/>
     <circle cx="21.4" cy="24.4" r="3.4" fill="${ST}"/><circle cx="18.4" cy="27.2" r="2.6" fill="${ST}"/>
     <circle cx="22.6" cy="23.2" r="1.6" fill="${L2}"/>
     <path d="M8.6 10.4c3 .6 5.4 3.2 5.6 6.4" stroke="${D}" stroke-width="1.8" fill="none" stroke-linecap="round"/>
     <path d="M8 8.2c-1.4 2-2 4.6-1.8 7" stroke="${L}" stroke-width="1.6" fill="none"/>`, [13, 9]),
  S('cut_drum_c', B(`<path fill="{f}" d="M9.6 6.4c4.6 0 8.4 3.6 8.4 8.4 0 3.4-1.6 5.6-3.6 7.2l-3.4 2.6c-2.6 2-6.6.4-6.6-3 0-1.6.6-2.6.6-4.4C5 12 5.6 6.4 9.6 6.4z"/>`) +
    `<path d="M16.6 19.4l3.4 3.4a3.4 3.4 0 1 1-4.8 4.8l-3.4-3.4z" fill="rgba(196,186,160,.95)"/>
     <circle cx="21.4" cy="24.4" r="3.4" fill="rgba(196,186,160,.95)"/><circle cx="18.4" cy="27.2" r="2.6" fill="rgba(196,186,160,.95)"/>` +
    CHAR('M6.2 12.2c3.4 1.2 6.4 1 9.2-.6M6 17c3.4 1.4 6.6 1.2 9.6-.8M7 21c2.6 1 5 .8 7.2-.6'), [13, 9]),
  // ribs: a WIDE rack with three bone ends — reads at 2x1
  S('cut_ribs', `<g fill="${ST}"><rect x="20" y="9.4" width="9" height="2.8" rx="1.4"/>
      <rect x="20" y="14.6" width="10" height="2.8" rx="1.4"/><rect x="20" y="19.8" width="8.6" height="2.8" rx="1.4"/></g>` +
    B(`<path fill="{f}" d="M4 9.4c0-2 1.4-3 3.4-3h11.2c2.4 0 4 1.4 4 3.6v12.4c0 2.2-1.6 3.6-4 3.6H7.4c-2 0-3.4-1-3.4-3z"/>`) +
    `<g stroke="${D}" stroke-width="1.4" fill="none"><path d="M9.4 7.4v17"/><path d="M15 7.4v17"/></g>
     <path d="M5.4 9c0-1.2.8-1.8 2-1.8h11" stroke="${L2}" stroke-width="1.4" fill="none"/>`, [15, 12]),
  S('cut_ribs_c', `<g fill="rgba(196,186,160,.95)"><rect x="20" y="9.4" width="9" height="2.8" rx="1.4"/>
      <rect x="20" y="14.6" width="10" height="2.8" rx="1.4"/><rect x="20" y="19.8" width="8.6" height="2.8" rx="1.4"/></g>` +
    B(`<path fill="{f}" d="M4 9.4c0-2 1.4-3 3.4-3h11.2c2.4 0 4 1.4 4 3.6v12.4c0 2.2-1.6 3.6-4 3.6H7.4c-2 0-3.4-1-3.4-3z"/>`) +
    CHAR('M5 11.6h17M5 16h17M5 20.4h17'), [15, 12]),
  // steak: a wide slab with a fat rim and marbling
  S('cut_steak', B(`<path fill="{f}" d="M4.6 12.6c1.6-4.4 6-6.6 11.4-6.6s10 2.2 11.4 6.6c1.4 4.4-1.4 8.8-5.4 10.6-3.2 1.4-8.8 1.6-12.2 0-4-1.8-6.8-6.2-5.2-10.6z"/>`) +
    `<path d="M4.6 12.6c1.6-4.4 6-6.6 11.4-6.6 2 0 4 .3 5.8 1-4.6.4-8 1.8-9.6 4.6-1.6 2.8-1 6.6 1.4 10.2-1.2-.2-2.4-.5-3.4-.9-4-1.8-7.2-3.9-5.6-8.3z" fill="rgba(206,199,180,.55)"/>
     <g fill="${D}" opacity=".5"><path d="M14 10.4c2.6.4 4 2 4.2 4.4M19.4 12.4c1.8.6 2.8 2 3 3.8M13.4 17c2.2.6 3.4 2 3.8 3.8"
       stroke="${D}" stroke-width="1.6" fill="none" stroke-linecap="round"/></g>`, [16, 12]),
  S('cut_steak_c', B(`<path fill="{f}" d="M4.6 12.6c1.6-4.4 6-6.6 11.4-6.6s10 2.2 11.4 6.6c1.4 4.4-1.4 8.8-5.4 10.6-3.2 1.4-8.8 1.6-12.2 0-4-1.8-6.8-6.2-5.2-10.6z"/>`) +
    CHAR('M6.2 11.4c6 2.6 13.4 2.6 19.6 0M4.8 16c7 2.8 15.4 2.8 22.4 0M7.4 20.6c5 2 11.2 2 16.2 0') +
    CHAR('M11.6 6.6c-1.4 5.4-1.4 11 0 16.4M20.4 6.6c1.4 5.4 1.4 11 0 16.4'), [16, 12]),

  // =========================================================================
  // MEDICAL / TOOLS
  // =========================================================================
  // sterile dressing: a sealed paper packet with a serrated tear edge
  S('bandage', B(`<path fill="{f}" d="M7.4 6.6h17.2v18.8H7.4z"/>`) +
    `<path d="M7.4 6.6l2.2 1.8 2.2-1.8 2.2 1.8 2.2-1.8 2.2 1.8 2.2-1.8 2.2 1.8 2-1.8v1.4l-2 1.8-2.2-1.8-2.2 1.8-2.2-1.8-2.2 1.8-2.2-1.8-2.2 1.8-2.2-1.8z" fill="${D2}"/>
     <path d="M7.4 25.4h17.2v-1.4l-2.2-1.8-2.2 1.8-2.2-1.8-2.2 1.8-2.2-1.8-2.2 1.8-2.2-1.8-2 1.8z" fill="${D2}"/>
     <rect x="9.6" y="11.6" width="12.8" height="9" fill="rgba(0,0,0,.24)"/>
     <g fill="#b8453c"><rect x="14.8" y="12.6" width="2.4" height="7"/><rect x="12" y="14.9" width="8" height="2.4"/></g>
     <rect x="8.4" y="7.6" width="1.5" height="17" fill="${L}"/>`, [16, 9.5]),
  S('tape', B(`<circle fill="{f}" cx="16" cy="16" r="11.4"/>`) +
    `<circle cx="16" cy="16" r="4.6" fill="rgba(0,0,0,.72)"/>
     <circle cx="16" cy="16" r="7.8" fill="none" stroke="${D}" stroke-width="1.5"/>
     <path d="M4.8 16.6c4 2.6 8.4 3.6 13.2 3.2" stroke="${D}" stroke-width="1.6" fill="none"/>
     <path d="M8 8.6c2-1.8 4.6-2.8 7.4-3" stroke="${L2}" stroke-width="1.8" fill="none" stroke-linecap="round"/>`, [16, 10]),
  // painkillers: a pill BOTTLE
  S('pills', `<rect x="10.6" y="3.4" width="10.8" height="4" rx=".8" fill="${D2}"/>` +
    B(`<path fill="{f}" d="M9.4 7.4h13.2v17.4a2.8 2.8 0 0 1-2.8 2.8h-7.6a2.8 2.8 0 0 1-2.8-2.8z"/>`) +
    `<rect x="11.4" y="12" width="9.2" height="10" rx="1" fill="rgba(198,196,188,.92)"/>
     <g fill="#b8453c"><rect x="15.2" y="14" width="1.8" height="6"/><rect x="13" y="16.1" width="6.2" height="1.8"/></g>
     <rect x="10.4" y="8.4" width="1.5" height="17" fill="${L}"/>`, [16, 8]),
  // antibiotics: a printed CARTON with a capsule mark — box, not bottle
  S('medbox', B(`<path fill="{f}" d="M5.6 11.4h17.2v14.8H5.6z"/>`) +
    `<path d="M5.6 11.4L11 6.6h17.2l-5.4 4.8z" fill="currentColor"/>
     <path d="M5.6 11.4L11 6.6h17.2l-5.4 4.8z" fill="${L2}"/>
     <path d="M22.8 11.4l5.4-4.8v14.8l-5.4 4.8z" fill="currentColor"/>
     <path d="M22.8 11.4l5.4-4.8v14.8l-5.4 4.8z" fill="rgba(0,0,0,.42)"/>
     <rect x="7.4" y="14" width="13.6" height="4.4" fill="rgba(198,196,188,.9)"/>
     <path d="M9.6 16.2a2.2 2.2 0 0 1 2.2-2.2h5.6a2.2 2.2 0 0 1 0 4.4h-5.6a2.2 2.2 0 0 1-2.2-2.2z" fill="#7b96a6"/>
     <path d="M14.6 14h2.8a2.2 2.2 0 0 1 0 4.4h-2.8z" fill="rgba(198,198,194,.9)"/>
     <g fill="rgba(0,0,0,.32)"><rect x="7.4" y="20.4" width="9.6" height="1.4"/><rect x="7.4" y="22.8" width="6.6" height="1.2"/></g>`,
  [16, 11]),
  S('tablets', B(`<path fill="{f}" d="M4 9.2h24a1.8 1.8 0 0 1 1.8 1.8v10a1.8 1.8 0 0 1-1.8 1.8H4a1.8 1.8 0 0 1-1.8-1.8V11A1.8 1.8 0 0 1 4 9.2z"/>`) +
    `<g fill="rgba(198,196,188,.94)"><circle cx="8.4" cy="13.6" r="2.4"/><circle cx="16" cy="13.6" r="2.4"/>
      <circle cx="23.6" cy="13.6" r="2.4"/><circle cx="8.4" cy="18.6" r="2.4"/><circle cx="16" cy="18.6" r="2.4"/>
      <circle cx="23.6" cy="18.6" r="2.4"/></g>
     <g fill="${D}" opacity=".45"><circle cx="8.4" cy="14.2" r="2.4"/><circle cx="16" cy="14.2" r="2.4"/>
      <circle cx="23.6" cy="14.2" r="2.4"/></g>
     <g fill="rgba(198,196,188,.94)"><circle cx="8.4" cy="13.6" r="2"/><circle cx="16" cy="13.6" r="2"/>
      <circle cx="23.6" cy="13.6" r="2"/></g>
     <rect x="2.2" y="10.4" width="27.6" height="1.2" fill="${L}"/>`, [16, 12]),
  S('rags', B(`<path fill="{f}" d="M4 12.5l6-6.5 5 3.5 5-4 8 6.5-3 5.5 3 6-6 5.5-5-3.5-5 4-8-6.5 3-5.5z"/>`) +
    `<g stroke="${D}" stroke-width="1.4" fill="none"><path d="M9 10l4 5-3 6 5 5"/><path d="M20 8l-2 7 4 4-3 6"/></g>
     <path d="M10.4 7.4l3.4 2.4" stroke="${L2}" stroke-width="1.4" fill="none"/>`, [16, 11]),
  S('wallet', B(`<path fill="{f}" d="M4 8h22a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4z"/>`) +
    `<path d="M18 14h11v6H18a3 3 0 0 1 0-6z" fill="${D}"/>
     <circle cx="21.6" cy="17" r="1.6" fill="${L2}"/>
     <path d="M4 8V6.6c0-1 .9-1.8 2-1.6l14 2.2" fill="${D}"/>
     <rect x="4.6" y="9" width="1.4" height="15" fill="${L}"/>`, [16, 11]),
  // rifle rounds: three TALL pointed cartridges
  S('ammo_r', B(`<g fill="{f}"><path d="M7 27.4V12l2.7-6 2.7 6v15.4z"/><path d="M14.6 27.4V12l2.7-6 2.7 6v15.4z"/>
      <path d="M22.2 27.4V12l2.7-6 2.7 6v15.4z"/></g>`) +
    `<g fill="${D}"><rect x="7" y="16.6" width="5.4" height="10.8"/><rect x="14.6" y="16.6" width="5.4" height="10.8"/>
      <rect x="22.2" y="16.6" width="5.4" height="10.8"/></g>
     <g fill="${L2}"><rect x="8" y="18.4" width="1.3" height="7"/><rect x="15.6" y="18.4" width="1.3" height="7"/>
      <rect x="23.2" y="18.4" width="1.3" height="7"/></g>`, [16, 11]),
  // pistol rounds: four SHORT stubby rounds, two rows
  S('ammo_p', B(`<g fill="{f}"><path d="M4.4 16.6v-3l1.9-3.4 1.9 3.4v3z"/><path d="M11.6 16.6v-3l1.9-3.4 1.9 3.4v3z"/>
      <path d="M18.8 16.6v-3l1.9-3.4 1.9 3.4v3z"/><path d="M26 16.6v-3l1.9-3.4 1.9 3.4v3z"/>
      <path d="M8 27v-3l1.9-3.4L11.8 24v3z"/><path d="M15.2 27v-3l1.9-3.4L19 24v3z"/>
      <path d="M22.4 27v-3l1.9-3.4L26.2 24v3z"/></g>`) +
    `<g fill="${D}"><rect x="4.4" y="13.6" width="3.8" height="3"/><rect x="11.6" y="13.6" width="3.8" height="3"/>
      <rect x="18.8" y="13.6" width="3.8" height="3"/><rect x="26" y="13.6" width="3.8" height="3"/>
      <rect x="8" y="24" width="3.8" height="3"/><rect x="15.2" y="24" width="3.8" height="3"/>
      <rect x="22.4" y="24" width="3.8" height="3"/></g>`, [16, 13]),
  S('cigs', B(`<path fill="{f}" d="M9 9.6h14v18.4a1.6 1.6 0 0 1-1.6 1.6h-10.8A1.6 1.6 0 0 1 9 28z"/>`) +
    `<rect x="9" y="14.6" width="14" height="5" fill="${D}"/>
     <g fill="rgba(200,196,186,.95)"><rect x="10.4" y="3" width="3.6" height="7" rx=".6"/>
       <rect x="15.4" y="2" width="3.6" height="8" rx=".6"/></g>
     <g fill="rgba(160,116,64,.95)"><rect x="10.4" y="7.6" width="3.6" height="2.4"/><rect x="15.4" y="7.6" width="3.6" height="2.4"/></g>
     <rect x="10" y="10.6" width="1.4" height="17" fill="${L}"/>`, [16, 8]),
  S('watch', `<rect x="12.4" y="1.4" width="7.2" height="9" rx="1.6" fill="rgba(58,46,34,.95)"/>
    <rect x="12.4" y="21.6" width="7.2" height="9" rx="1.6" fill="rgba(58,46,34,.95)"/>` +
    B(`<circle fill="{f}" cx="16" cy="16" r="9.2"/>`) +
    `<circle cx="16" cy="16" r="6.6" fill="rgba(198,196,188,.95)"/>
     <circle cx="16" cy="16" r="6.6" fill="none" stroke="${D}" stroke-width=".8"/>
     <path d="M16 11.6V16l3.2 2.4" stroke="rgba(0,0,0,.75)" stroke-width="1.4" fill="none" stroke-linecap="round"/>
     <path d="M10.4 10.4a8 8 0 0 1 5-2.6" stroke="${L2}" stroke-width="1.6" fill="none" stroke-linecap="round"/>`, [16, 8]),
  S('matches', B(`<path fill="{f}" d="M6 15h20v13a1.6 1.6 0 0 1-1.6 1.6H7.6A1.6 1.6 0 0 1 6 28z"/>`) +
    `<rect x="6" y="19.6" width="20" height="4.6" fill="${D}"/>
     <g><rect x="11.8" y="3" width="2.3" height="12" rx="1" fill="rgba(198,190,167,.95)"/>
       <circle cx="12.9" cy="3.4" r="2.3" fill="#b8453c"/>
       <rect x="17.2" y="5" width="2.3" height="10" rx="1" fill="rgba(198,190,167,.95)"/>
       <circle cx="18.3" cy="5.4" r="2.3" fill="#b8453c"/></g>
     <rect x="6.8" y="16" width="1.4" height="12" fill="${L}"/>`, [16, 11]),
  // cooking stick: a 1x3 item, so the body is re-hung VERTICAL (the diagonal it
  // was drawn on could not survive the 1:3 canvas crop) — same paths, rotated
  // about the canvas centre and scaled to sit inside the 32-unit height.
  S('stick', '<g transform="translate(16 16) rotate(-42.6) scale(.88) translate(-16 -16)">' +
    B(`<path fill="{f}" d="M6.5 27.4l1.9 1.9L27 10.6c1-1 1.6-2.4 1.6-3.6l-.2-3.4-3.4-.2c-1.2 0-2.6.6-3.6 1.6z"/>`) +
    `<path d="M9 25l16-16" stroke="${D}" stroke-width="1.2" opacity=".7"/>
     <path d="M28.4 3.6l-4.6 4.6" stroke="${D}" stroke-width="1.4"/></g>`, null),

  // =========================================================================
  // CLOTHING
  // =========================================================================
  S('tshirt', B(`<path fill="{f}" d="M11.5 4L6 7l-2.6 6 4 2 1.6-2.6V28h14V12.4l1.6 2.6 4-2L26 7l-5.5-3c-.7 1.9-2.4 3-4.5 3s-3.8-1.1-4.5-3z"/>`) +
    `<path d="M11.5 4c.7 1.9 2.4 3 4.5 3s3.8-1.1 4.5-3" fill="none" stroke="${D}" stroke-width="1.4"/>
     <path d="M11.5 4c.7 1.9 2.4 3 4.5 3s3.8-1.1 4.5-3" fill="none" stroke="rgba(255,255,255,.10)" stroke-width=".8" transform="translate(0,1.4)"/>
     <path d="M9 24h14v4H9z" fill="${D}" opacity=".5"/>` +
    // 2x2: raglan shoulder seams, sleeve hems, a washed-out crease, hem stitch
    SEAM('M11 5.6L9.6 13M21 5.6l1.4 7.4') + SEAM('M7.6 14.6l-1.2 1.6M24.4 14.6l1.2 1.6') +
    SEAM('M9.4 25.2h13.2') + WEAR('M12.6 16.4c1.6 2.4 5 2.6 7 .6', 1.2), [16, 11]),
  S('shirt', B(`<path fill="{f}" d="M11.5 4L6 7l-2.6 6 4 2 1.6-2.6V28h14V12.4l1.6 2.6 4-2L26 7l-5.5-3-4.5 4z"/>`) +
    // flannel: a real check weave, then collar, placket, buttons, chest pockets
    `<g opacity=".5"><g fill="rgba(0,0,0,.30)"><rect x="8" y="8" width="1.6" height="20"/>
       <rect x="12.4" y="8" width="1.6" height="20"/><rect x="18" y="8" width="1.6" height="20"/>
       <rect x="22.4" y="8" width="1.6" height="20"/></g>
      <g fill="rgba(0,0,0,.30)"><rect x="8" y="10.4" width="16" height="1.6"/>
       <rect x="8" y="16" width="16" height="1.6"/><rect x="8" y="21.6" width="16" height="1.6"/></g>
      <g fill="rgba(255,255,255,.09)"><rect x="10.4" y="8" width=".9" height="20"/>
       <rect x="20.6" y="8" width=".9" height="20"/></g></g>
     <path d="M11.5 4L16 8l4.5-4L16 5.6z" fill="${L2}"/>
     <path d="M11.5 4L16 8l4.5-4" fill="none" stroke="${D}" stroke-width=".8"/>
     <rect x="15" y="7" width="2" height="21" fill="${D}"/>
     <g fill="rgba(206,204,192,.5)"><circle cx="16" cy="12" r=".75"/><circle cx="16" cy="17.4" r=".75"/>
      <circle cx="16" cy="22.8" r=".75"/></g>
     <g fill="${D}" opacity=".55"><rect x="9" y="12" width="5" height="4.2" rx=".4"/>
      <rect x="18" y="12" width="5" height="4.2" rx=".4"/></g>` +
    SEAM('M9 12.4h5M18 12.4h5') + SEAM('M11 5.6L9.6 13M21 5.6l1.4 7.4') +
    SEAM('M9.4 26.6h13.2'), [16, 11]),
  // 3x3 field jacket — the largest garment plate on the screen, so it carries a
  // full second tier: storm collar, zip, flapped chest AND hip pockets with
  // hardware, sleeve seams, elbow wear, drawcord hem.
  S('jacket', B(`<path fill="{f}" d="M11 3L4.5 6.5 2.5 15l4 1.6L8 13.6V29h16V13.6l1.5 3 4-1.6-2-8.5L21 3l-5 4.6z"/>`) +
    `<path d="M11 3l5 4.6L21 3l-5 1.6z" fill="${L2}"/>
     <path d="M11 3l5 4.6L21 3" fill="none" stroke="${D2}" stroke-width=".9"/>` +
    ZIP('M16 7.6V28.6') +
    `<g fill="${D}"><rect x="9.2" y="11.4" width="5.2" height="4.2" rx=".5"/>
       <rect x="17.6" y="11.4" width="5.2" height="4.2" rx=".5"/>
       <rect x="9" y="19.4" width="5.6" height="5" rx=".5"/><rect x="17.4" y="19.4" width="5.6" height="5" rx=".5"/></g>
     <g fill="rgba(0,0,0,.30)"><rect x="9.2" y="11.4" width="5.2" height="1.5" rx=".4"/>
       <rect x="17.6" y="11.4" width="5.2" height="1.5" rx=".4"/>
       <rect x="9" y="19.4" width="5.6" height="1.7" rx=".4"/><rect x="17.4" y="19.4" width="5.6" height="1.7" rx=".4"/></g>` +
    BUCKLE(10.8, 12.4, 2, 1.2) + BUCKLE(19.2, 12.4, 2, 1.2) +
    `<path d="M8 26.4h16v2.6H8z" fill="${D}" opacity=".55"/>` +
    SEAM('M11 4.4L8.4 14M21 4.4l2.6 9.6') + SEAM('M6.6 15.4l-1.6 1.4M25.4 15.4l1.6 1.4') +
    SEAM('M8.4 26.8h15.2') + WEAR('M6.2 9.4c-.9 1.9-1.4 3.6-1.6 5.2', 1.2) +
    WEAR('M25.8 9.4c.9 1.9 1.4 3.6 1.6 5.2', 1.2), [16, 12]),
  // 2x3 work pants: waistband + belt loops, fly, back pockets with rivets,
  // knee crease wear, cuff stitching
  S('pants', B(`<path fill="{f}" d="M8 3h16v6l-1.4 20h-5L16 15l-1.6 14h-5L8 9z"/>`) +
    `<rect x="8" y="3" width="16" height="3.4" fill="${D}"/>
     <g fill="${D2}"><rect x="10" y="2.6" width="1.3" height="4.4" rx=".3"/>
       <rect x="15.4" y="2.6" width="1.3" height="4.4" rx=".3"/>
       <rect x="20.8" y="2.6" width="1.3" height="4.4" rx=".3"/></g>
     <g fill="${D}" opacity=".6"><rect x="9.4" y="9" width="4.4" height="5" rx=".6"/>
      <rect x="18.2" y="9" width="4.4" height="5" rx=".6"/></g>
     <g fill="rgba(196,190,168,.32)"><circle cx="9.8" cy="9.4" r=".55"/><circle cx="13.4" cy="9.4" r=".55"/>
      <circle cx="18.6" cy="9.4" r=".55"/><circle cx="22.2" cy="9.4" r=".55"/></g>
     <path d="M16 15l.6 5" stroke="${D2}" stroke-width="1.2"/>` +
    ZIP('M16.4 6.6v3.6') +
    SEAM('M8.2 6.8h15.6') + SEAM('M9.4 9h4.4M18.2 9h4.4') +
    SEAM('M11.4 28.6h4.4M18.4 28.6h4.2') +
    WEAR('M10.4 17.6c1.4.6 2.8.6 4 0', 1.3) + WEAR('M18.2 17.6c1.3.6 2.7.6 3.9 0', 1.3), [16, 10]),
  S('shorts', B(`<path fill="{f}" d="M7 7h18v5l-1.6 12h-5.6L16 17l-1.8 7H8.6L7 12z"/>`) +
    `<rect x="7" y="7" width="18" height="3.2" fill="${D}"/>
     <g fill="${D2}"><rect x="10.4" y="6.6" width="1.2" height="4.2" rx=".3"/>
       <rect x="19.6" y="6.6" width="1.2" height="4.2" rx=".3"/></g>
     <path d="M16 17l.6 3.4" stroke="${D2}" stroke-width="1.2"/>` +
    ZIP('M16.2 10.4v3') + SEAM('M7.4 10.6h17.2') + SEAM('M9.6 23h6.2M17.8 23h5.4'), [16, 10]),
  // messenger/courier bag: flap-over, one long strap
  S('bag_courier', `<g fill="none" stroke-linecap="round" stroke-width="2.4">
      <path d="M6 12.6C6 7.4 9.4 4.4 14 4.4" stroke="currentColor"/>
      <path d="M6 12.6C6 7.4 9.4 4.4 14 4.4" stroke="rgba(0,0,0,.42)"/></g>` +
    B(`<path fill="{f}" d="M4.6 12.6h22.8v11a3 3 0 0 1-3 3H7.6a3 3 0 0 1-3-3z"/>`) +
    `<path d="M4.6 12.6h22.8v5.6H4.6z" fill="currentColor"/>
     <path d="M4.6 12.6h22.8v5.6H4.6z" fill="${L}"/>
     <path d="M4.6 18.2h22.8v1.2H4.6z" fill="${D2}"/>
     <rect x="13.6" y="17" width="4.8" height="3.6" rx=".8" fill="${D2}"/>
     <g fill="${D2}"><rect x="8.4" y="17.6" width="2.4" height="4.6" rx=".4"/>
       <rect x="21.2" y="17.6" width="2.4" height="4.6" rx=".4"/></g>` +
    BUCKLE(8.5, 19.6, 2.2, 1.4) + BUCKLE(21.3, 19.6, 2.2, 1.4) +
    SEAM('M5.4 13.6h21.2') + SEAM('M6 25h20') + SEAM('M12.4 12.8v5.2M19.6 12.8v5.2') +
    WEAR('M6.4 20.6v3', 1.2), [16, 12]),
  // top-loading hiking pack: lid strap + side pockets
  S('bag_hiking', B(`<path fill="{f}" d="M8.4 10a7.6 7.6 0 0 1 15.2 0v16.4a2.4 2.4 0 0 1-2.4 2.4H10.8a2.4 2.4 0 0 1-2.4-2.4z"/>`) +
    `<path d="M8.4 10.6a7.6 7.6 0 0 1 15.2 0v3.4c0 1.4-3.4 2.2-7.6 2.2s-7.6-.8-7.6-2.2z" fill="${D}"/>
     <rect x="14.4" y="12.4" width="3.2" height="4.4" rx=".8" fill="${D2}"/>
     <path d="M8.4 19h15.2v5.4H8.4z" fill="${D}" opacity=".55"/>
     <g fill="none" stroke-linecap="round" stroke-width="2.4">
       <path d="M6.2 13.4C4.4 15 3.8 18 4.4 21M25.8 13.4c1.8 1.6 2.4 4.6 1.8 7.6" stroke="currentColor"/>
       <path d="M6.2 13.4C4.4 15 3.8 18 4.4 21M25.8 13.4c1.8 1.6 2.4 4.6 1.8 7.6" stroke="rgba(0,0,0,.42)"/></g>
     <rect x="10" y="8.6" width="1.6" height="18" fill="${L}"/>` +
    // 4x4 — the biggest plate in the container column. Lid strap, compression
    // ladder down both sides, a daisy chain of webbing loops, base panel.
    `<g fill="${D2}"><rect x="15.1" y="7.2" width="1.8" height="6.4" rx=".4"/>
       <rect x="8.7" y="20.2" width="14.6" height="1.5" rx=".3"/>
       <rect x="8.7" y="23" width="14.6" height="1.5" rx=".3"/></g>` +
    BUCKLE(14.6, 10.4, 2.8, 1.8) + BUCKLE(10.2, 20, 2.6, 1.9) + BUCKLE(19.2, 20, 2.6, 1.9) +
    BUCKLE(10.2, 22.8, 2.6, 1.9) + BUCKLE(19.2, 22.8, 2.6, 1.9) +
    `<path d="M8.4 26.2h15.2v2.6H8.4z" fill="${D}" opacity=".5"/>` +
    SEAM('M8.6 18.9h14.8') + SEAM('M8.6 26.6h14.8') +
    SEAM('M11.4 17.4V26M20.6 17.4V26') +
    WEAR('M9.6 11.4c-.7 1.6-1 3.2-1 4.6', 1.2) + WEAR('M22.4 11.4c.7 1.6 1 3.2 1 4.6', 1.2), [16, 9.5]),
  // expedition frame pack: tall body, top lid, bottom compartment, side straps
  S('bag_expedition', B(`<path fill="{f}" d="M7 8.6a9 9 0 0 1 18 0v18a2.6 2.6 0 0 1-2.6 2.6H9.6A2.6 2.6 0 0 1 7 26.6z"/>`) +
    `<path d="M7 9.4a9 9 0 0 1 18 0v2.8c0 1.5-4 2.4-9 2.4s-9-.9-9-2.4z" fill="${D}"/>
     <path d="M7 22h18v4.6a2.6 2.6 0 0 1-2.6 2.6H9.6A2.6 2.6 0 0 1 7 26.6z" fill="${D}"/>
     <g fill="${D2}"><rect x="14.2" y="10.4" width="3.6" height="4.4" rx=".8"/>
       <rect x="9" y="16.4" width="14" height="1.5"/><rect x="9" y="19.2" width="14" height="1.5"/></g>
     <g fill="none" stroke-linecap="round" stroke-width="2.6">
       <path d="M4.6 12C2.6 14 1.8 17.6 2.6 21M27.4 12c2 2 2.8 5.6 2 9" stroke="currentColor"/>
       <path d="M4.6 12C2.6 14 1.8 17.6 2.6 21M27.4 12c2 2 2.8 5.6 2 9" stroke="rgba(0,0,0,.42)"/></g>
     <rect x="8.8" y="7.4" width="1.6" height="20" fill="${L}"/>` +
    // 5x5 — draws even bigger than the hiking pack. External frame rails, lid
    // strap, a full compression ladder, base compartment seam.
    `<g fill="${D2}"><rect x="15.1" y="6" width="1.8" height="6" rx=".4"/>
       <rect x="6.2" y="13" width="1.3" height="14" rx=".5"/><rect x="24.5" y="13" width="1.3" height="14" rx=".5"/></g>` +
    BUCKLE(14.6, 9, 2.8, 1.8) + BUCKLE(9.4, 16.2, 2.8, 1.9) + BUCKLE(19.8, 16.2, 2.8, 1.9) +
    BUCKLE(9.4, 19, 2.8, 1.9) + BUCKLE(19.8, 19, 2.8, 1.9) +
    SEAM('M7.6 21.9h16.8') + SEAM('M7.6 27.4h16.8') + SEAM('M12.4 14.6V27M19.6 14.6V27') +
    WEAR('M8.4 9.6c-.8 1.8-1.2 3.6-1.4 5.2', 1.2) +
    WEAR('M23.6 9.6c.8 1.8 1.2 3.6 1.4 5.2', 1.2), [16, 10.5]),

  // =========================================================================
  // WEAPONS (hands slot)
  // =========================================================================
  S('rifle', B(`<path fill="{f}" d="M2 13h24v4H12l-1.6 2.6H6.4L5 17H2z"/>`) +
    B(`<path fill="{f}" d="M13 17l-2.6 8.6c-.4 1.4.4 2.4 1.8 2.4h3l3-11z"/>`) +
    `<rect x="20" y="10.4" width="8" height="2.4" rx="1" fill="${D2}"/>
     <rect x="26" y="12.6" width="4" height="4.4" rx="1" fill="currentColor"/>
     <rect x="6" y="10" width="9" height="1.8" rx=".9" fill="${D2}"/>`, null),
  S('pistol', B(`<path fill="{f}" d="M4 10h24v5H18l-1 2h-3l-1-2H4z"/>`) +
    B(`<path fill="{f}" d="M12 17l-3 10c-.3 1.2.5 2 1.7 2h3.4l3.4-12z"/>`) +
    `<rect x="4" y="8.4" width="20" height="2" rx="1" fill="${D2}"/>
     <rect x="14.4" y="15" width="2.4" height="4" rx="1" fill="${D2}"/>`, null),
  S('knife', B(`<path fill="{f}" d="M3 20l16-14 2.6 2.6L8 24z"/>`) +
    `<path d="M3 20l16-14" stroke="${L2}" stroke-width="1"/>
     <path d="M8.6 24.6l4.4-4.4 5 5-4.4 4.4a1.6 1.6 0 0 1-2.2 0l-2.8-2.8a1.6 1.6 0 0 1 0-2.2z" fill="rgba(48,40,32,.95)"/>`, null),
  S('axe', B(`<path fill="{f}" d="M13 4c5.4 0 10 3.6 11.6 8.6L26 17l-4-1-4.6 2c-1-5.2-3.4-9.6-6.4-12.6z"/>`) +
    `<rect x="6.6" y="12" width="3.6" height="18" rx="1.4" fill="${WOOD}" transform="rotate(-18 8.4 21)"/>
     <path d="M17.4 18L26 17" stroke="${D}" stroke-width="1.4"/>`, null),
  S('bat', B(`<path fill="{f}" d="M25.8 3.4c2 2 1.6 4.4-1 7L13 22.2 8.8 18 20.6 6.2c2.6-2.6 5-3 5.2-2.8z"/>`) +
    `<rect x="3.4" y="19.6" width="7.6" height="4.6" rx="2.3" fill="${D2}" transform="rotate(45 7.2 21.9)"/>`, null),
  S('spear', B(`<path fill="{f}" d="M27 2l-2.2 7.2-4.6 4.6-2.6-2.6 4.6-4.6z"/>`) +
    `<rect x="2" y="16.6" width="22" height="2.8" rx="1.4" fill="${WOOD}" transform="rotate(45 13 18)"/>`, null),
  S('hand', B(`<path fill="{f}" d="M10 15V6.5a2 2 0 0 1 4 0V14h1V4.5a2 2 0 0 1 4 0V14h1V6a2 2 0 0 1 4 0v9.6h1v-4a1.8 1.8 0 0 1 3.6 0v8.6c0 4.6-3.8 8.4-8.4 8.4h-3.4c-2.2 0-4.2-1-5.6-2.8l-5-6.6a2 2 0 0 1 3-2.6z"/>`), null),
  // generic fallback
  S('crate', B(`<path fill="{f}" d="M5 8h22v18H5z"/>`) +
    `<path d="M5 8h22v3.4H5zM5 22.6h22V26H5z" fill="${D}"/>
     <path d="M5 8l22 18M27 8L5 26" stroke="${D}" stroke-width="1.4" opacity=".5"/>`, [16, 11]),
].join('') + '</defs></svg>';

// item id -> [symbol, tint]. Tints are grimy, low-chroma survival-game colors:
// they SUPPORT the silhouette, they never carry identity on their own.
const MAP = {
  canned_stew: ['can_tall', '#7f8570'], canned_fish: ['tin_flat', '#8d979b'],
  canned_veg: ['can_wide', '#6f7d61'], canned_soup: ['can_ribbed', '#8f8262'],
  canned_fruit: ['jar_fruit', '#9a7f4e'], bulging_can: ['candent', '#767b5d'],
  crackers: ['crackers', '#a68f5c'], candy_bar: ['candy', '#8f5c33'],
  soda_can: ['drinkcan', '#96453a'], juice_can: ['drinkcan', '#a87f31'],
  vodka_bottle: ['bottle_glass', '#a8b0a5'], murky_water: ['bottle_murky', '#6a6e53'],
  water_bottle: ['bottle_plastic', '#8b9ea3'],

  mush_porcini: ['mu_porcini', '#9a7049'], mush_chanterelle: ['mu_chant', '#c08a34'],
  mush_birch_bolete: ['mu_bolete', '#846140'], mush_puffball: ['mu_puff', '#bdb9a7'],
  mush_morel: ['mu_morel', '#8a7150'], mush_morel_roasted: ['mu_morel_roast', '#6b5033'],
  mush_fly_agaric: ['mu_agaric', '#a83c33'], mush_death_cap: ['mu_death', '#aab98e'],
  mush_false_morel: ['mu_false', '#8e5b41'], mush_false_morel_roasted: ['mu_false_roast', '#66422e'],

  berry_blueberry: ['be_blue', '#4d5f85'], berry_raspberry: ['be_rasp', '#9c4450'],
  berry_lingonberry: ['be_lingon', '#a03432'], berry_elderberry: ['be_elder', '#453a55'],
  berry_elderberry_cooked: ['stew_pan', '#5c5a56'], berry_rosehip: ['be_rose', '#ad4f33'],
  berry_rowan: ['be_rowan', '#b0631f'], berry_rowan_cooked: ['stew_mug', '#a8622c'],
  fruit_apple: ['apple', '#8b9e43'],

  meat_raw_chicken: ['cut_drum', '#b07c74'], meat_cooked_chicken: ['cut_drum_c', '#8e5a2c'],
  meat_raw_mutton: ['cut_ribs', '#96504c'], meat_cooked_mutton: ['cut_ribs_c', '#77452a'],
  meat_raw_game: ['cut_steak', '#83433f'], meat_cooked_game: ['cut_steak_c', '#653c24'],

  bandage: ['bandage', '#beb9a9'], duct_tape: ['tape', '#8e949a'],
  painkillers: ['pills', '#b0b6ac'], antibiotics: ['medbox', '#8d9ea3'],
  purification_tablets: ['tablets', '#728a94'],
  rags: ['rags', '#847e70'], wallet: ['wallet', '#6f5237'],
  ammo_pistol: ['ammo_p', '#a68e55'], ammo_rifle: ['ammo_r', '#9c8045'],
  cigarettes: ['cigs', '#ada492'], pocket_watch: ['watch', '#b3945c'],
  matches: ['matches', '#9d7343'], cooking_stick: ['stick', '#7f6642'],

  tshirt: ['tshirt', '#9fa295'], flannel_shirt: ['shirt', '#805a41'],
  rain_jacket: ['jacket', '#465e69'], field_jacket: ['jacket', '#636e4a'],
  work_pants: ['pants', '#6e6149'], shorts: ['shorts', '#7b7661'],
  backpack_courier: ['bag_courier', '#71644a'], backpack_hiking: ['bag_hiking', '#4f593b'],
  backpack_expedition: ['bag_expedition', '#45423c'],
};

// kind fallback so a brand-new item still draws SOMETHING sane
const BY_KIND = {
  food: ['can_tall', '#7f8570'], drink: ['bottle_plastic', '#8b9ea3'], med: ['pills', '#b0b6ac'],
  tool: ['crate', '#8e949a'], valuable: ['watch', '#b3945c'],
  shirt: ['tshirt', '#9fa295'], jacket: ['jacket', '#636e4a'],
  pants: ['pants', '#6e6149'], backpack: ['bag_hiking', '#4f593b'],
};

export const WEAPON_ICON = {
  fists: 'hand', knife: 'knife', bat: 'bat', axe: 'axe',
  spear: 'spear', pistol: 'pistol', rifle: 'rifle',
};

export function iconFor(id) {
  const m = MAP[id];
  if (m) return { sym: m[0], tint: m[1] };
  const k = BY_KIND[ITEMS[id]?.kind];
  return k ? { sym: k[0], tint: k[1] } : { sym: 'crate', tint: '#8e949a' };
}

// smoke hook: every catalog item must have hand-authored art, not a fallback
export function hasIcon(id) { return !!MAP[id]; }
export function missingIcons() { return Object.keys(ITEMS).filter((id) => !MAP[id]); }

// ---------------------------------------------------------------------------
// CI gate for the round-4 defect "50 items drawn by 30 symbols, separated by a
// 5% hue shift". Two items sharing a symbol are only legal when the tints are
// far apart AND their grid footprints differ; otherwise they are the same cell
// on a near-black grid and the player cannot tell them apart.
// ---------------------------------------------------------------------------
const rgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
// cheap perceptual distance (weighted RGB, Compuphase) — good enough as a gate
function tintDist(a, b) {
  const [r1, g1, b1] = rgb(a), [r2, g2, b2] = rgb(b);
  const rm = (r1 + r2) / 2, dr = r1 - r2, dg = g1 - g2, db = b1 - b2;
  return Math.sqrt((2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db);
}
export const TINT_MIN_DIST = 60;
export function iconCollisions() {
  const ids = Object.keys(MAP);
  const out = [];
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      const a = ids[i], b = ids[j];
      if (MAP[a][0] !== MAP[b][0]) continue;
      const d = tintDist(MAP[a][1], MAP[b][1]);
      if (d >= TINT_MIN_DIST) continue;
      const sa = ITEMS[a]?.size, sb = ITEMS[b]?.size;
      if (sa && sb && (sa[0] !== sb[0] || sa[1] !== sb[1])) continue; // footprint separates them
      out.push([a, b, +d.toFixed(1)]);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Canvas-aspect drift gate (round-5 critique, defect #2). An item whose glyph
// canvas is much squarer than its footprint letterboxes inside its plate, which
// is the dead-plate defect coming back in a quieter form. This reports the
// offenders instead of asserting, because two of them are legitimate and a
// hard gate would have to be silenced anyway:
//   * a symbol shared by items of DIFFERENT footprints (`jacket` is worn by the
//     2x3 rain jacket and the 3x3 field jacket) has one shape and cannot match
//     both — the critique's proposed `S(id)` reading `ITEMS[id].size` assumes
//     one symbol per item, which this sheet has never been;
//   * the 2x1 meat cuts are drawn with a contact shadow at cy 29.2, so their
//     canvas cannot be cropped in y without cutting the shadow off; they need
//     re-authored wide bodies, not a viewBox change.
// ratio > 1 means the glyph is squarer than the plate it sits in.
export function iconAspectReport() {
  const out = [];
  for (const id of Object.keys(MAP)) {
    const size = ITEMS[id]?.size;
    if (!size) continue;
    const want = size[0] / size[1];
    const have = iconAspect(MAP[id][0]);
    const ratio = Math.max(want / have, have / want);
    if (ratio > 1.35) out.push([id, MAP[id][0], `${size[0]}x${size[1]}`, +ratio.toFixed(2)]);
  }
  return out;
}
